import { Component, ComponentRef, inject, OnDestroy, OnInit, ViewContainerRef } from "@angular/core";
import { SlotService } from "common/slot.service";
import { BaseComponent } from "process/view/base.component";
import { ComponentService } from "process/view/component.service";
import { ComponentType } from "process/view/type.interface";
import { SlotDirective } from "common/slot.directive";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LayoutService, Size } from "core/layout.service";

@Component({
	selector: "stages-responsive-widget-container",
	templateUrl: "responsive-widget-container.component.html",
	providers: [SlotService],
	imports: [SlotDirective],
})
export class ResponsiveWidgetContainerComponent extends BaseComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<boolean>();
	private viewContainerRef!: ViewContainerRef;
	private layoutService = inject(LayoutService);

	private currentComponentRef?: ComponentRef<unknown>;

	constructor() {
		const componentService = inject(ComponentService);
		const slotService = inject(SlotService);

		super(componentService, slotService);
	}

	protected override onSlotCreated(slotName: string, viewContainerRef: ViewContainerRef): void {
		this.viewContainerRef = viewContainerRef;
		setTimeout(() => this.update());
	}

	ngOnInit(): void {
		this.layoutService.size.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.update();
		});
	}

	override ngOnDestroy(): void {
		this.currentComponentRef?.destroy();
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	private update(): void {
		if (!this.type?.components) {
			return;
		}

		let foundMatchingQuery = false;
		for (const component of this.type.components.sort((a, b) => {
			const aWidth = a.media ? (this.layoutService.getMinWidth(a.media as Size) ?? 0) : 0;
			const bWidth = b.media ? (this.layoutService.getMinWidth(b.media as Size) ?? 0) : 0;
			return bWidth - aWidth;
		})) {
			if (component.media && this.layoutService.isAtLeast(component.media as Size) && !foundMatchingQuery) {
				this.show(component);
				foundMatchingQuery = true;
			}
		}

		if (!foundMatchingQuery) {
			const defaultComponent = this.getDefaultComponent();
			if (defaultComponent) {
				this.show(defaultComponent);
			}
		}
	}

	private show(component: ComponentType): void {
		if (this.viewContainerRef) {
			this.viewContainerRef.clear();
			this.currentComponentRef = this.componentService.appendComponent(component, this.viewContainerRef);
		}
	}

	private getDefaultComponent(): ComponentType | undefined {
		return this.type?.components ? this.type.components.find((comp) => !comp.media) : undefined;
	}
}
